import { useState } from "react";
import { router } from "@inertiajs/react";
import Header from "../Component/Header";

const EMPTY_VARIANT = {
    prod_var_name: "",
    total_earned_coin: 0,
    prod_var_price: "",
    prod_var_bonus: "",
    prod_var_bonus_type: 0,
    reseller_commission_type: 2,
    reseller_commission_value: "",
    reseller_commission_status: 0,
    prod_var_type: 0,
    prod_var_status: 1,
};

export default function Detail({ data }) {
    const initialImageUrl = data?.prod_image ? `/storage/${data.prod_image}` : null;
    const [form, setForm] = useState({
        prod_name: data?.prod_name ?? "",
        prod_status: data?.prod_status ?? 1,
        prod_image: null,
        remove_prod_image: false,
        variants:
            data?.variants?.length > 0
                ? data.variants.map((variant) => ({
                      prod_var_id: variant.prod_var_id,
                      prod_var_name: variant.prod_var_name ?? "",
                      total_earned_coin: variant.total_earned_coin ?? 0,
                      prod_var_price: variant.prod_var_price ?? "",
                      prod_var_bonus: variant.prod_var_bonus ?? "",
                      prod_var_bonus_type: variant.prod_var_bonus_type ?? 0,
                      reseller_commission_type: variant.reseller_commission_type ?? 2,
                      reseller_commission_value: variant.reseller_commission_value ?? "",
                      reseller_commission_status: variant.reseller_commission_status ?? 0,
                      prod_var_type: variant.prod_var_type ?? 0,
                      prod_var_status: variant.prod_var_status ?? 1,
                  }))
                : [{ ...EMPTY_VARIANT }],
    });
    const [imagePreview, setImagePreview] = useState(initialImageUrl);

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const setImageFile = (file) => {
        setForm((prev) => ({
            ...prev,
            prod_image: file,
            remove_prod_image: false,
        }));
        if (file) {
            setImagePreview(URL.createObjectURL(file));
        } else {
            setImagePreview(initialImageUrl);
        }
    };

    const setVariantField = (index, field, value) => {
        setForm((prev) => ({
            ...prev,
            variants: prev.variants.map((variant, i) =>
                i === index ? { ...variant, [field]: value } : variant
            ),
        }));
    };

    const addVariant = () => {
        setForm((prev) => ({
            ...prev,
            variants: [...prev.variants, { ...EMPTY_VARIANT }],
        }));
    };

    const removeVariant = (index) => {
        setForm((prev) => ({
            ...prev,
            variants: prev.variants.filter((_, i) => i !== index),
        }));
    };

    const getError = (path) => errors[path];

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setErrors({});

        const preparedVariants = form.variants
            .filter(
                (variant) =>
                    variant.prod_var_name.trim() !== "" ||
                    String(variant.prod_var_price).trim() !== "" ||
                    String(variant.prod_var_bonus).trim() !== "" ||
                    variant.prod_var_id
            )
            .map((variant) => ({
                ...(variant.prod_var_id ? { prod_var_id: variant.prod_var_id } : {}),
                prod_var_name: variant.prod_var_name,
                total_earned_coin:
                    variant.total_earned_coin === "" ? 0 : Number(variant.total_earned_coin),
                prod_var_price: variant.prod_var_price,
                prod_var_bonus: variant.prod_var_bonus === "" ? 0 : Number(variant.prod_var_bonus),
                prod_var_bonus_type: Number(variant.prod_var_bonus_type ?? 0),
                reseller_commission_type: Number(variant.reseller_commission_type ?? 2),
                reseller_commission_value:
                    variant.reseller_commission_value === ""
                        ? 0
                        : Number(variant.reseller_commission_value),
                reseller_commission_status: Number(variant.reseller_commission_status ?? 0),
                prod_var_type: Number(variant.prod_var_type ?? 0),
                prod_var_status: Number(variant.prod_var_status),
            }));

        const payload = new FormData();
        payload.append("_method", "PUT");
        payload.append("prod_name", form.prod_name);
        payload.append("prod_status", String(Number(form.prod_status)));
        payload.append("remove_prod_image", form.remove_prod_image ? "1" : "0");
        if (form.prod_image) {
            payload.append("prod_image", form.prod_image);
        }
        preparedVariants.forEach((variant, index) => {
            if (variant.prod_var_id) {
                payload.append(`variants[${index}][prod_var_id]`, String(variant.prod_var_id));
            }
            payload.append(`variants[${index}][prod_var_name]`, variant.prod_var_name);
            payload.append(`variants[${index}][total_earned_coin]`, String(variant.total_earned_coin));
            payload.append(`variants[${index}][prod_var_price]`, String(variant.prod_var_price));
            payload.append(`variants[${index}][prod_var_bonus]`, String(variant.prod_var_bonus));
            payload.append(`variants[${index}][prod_var_bonus_type]`, String(variant.prod_var_bonus_type));
            payload.append(`variants[${index}][reseller_commission_type]`, String(variant.reseller_commission_type));
            payload.append(`variants[${index}][reseller_commission_value]`, String(variant.reseller_commission_value));
            payload.append(`variants[${index}][reseller_commission_status]`, String(variant.reseller_commission_status));
            payload.append(`variants[${index}][prod_var_type]`, String(variant.prod_var_type));
            payload.append(`variants[${index}][prod_var_status]`, String(variant.prod_var_status));
        });

        try {
            await window.axios.post(`/panel/products/${data.prod_id}`, payload, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "multipart/form-data",
                },
            });
            router.visit(`/panel/products/${data.prod_id}`);
        } catch (error) {
            const validationErrors = error?.response?.data?.errors ?? {};
            setErrors(validationErrors);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-5">
                            <div className="inline-flex sm:justify-end items-center gap-x-3">
                                <p className="text-sm text-muted-foreground-1">Product</p>
                            </div>
                        </div>

                        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-6 gap-5">
                            <div className="lg:col-span-4 space-y-4">
                                <div className="flex flex-col bg-card border border-card-line overflow-hidden rounded-xl shadow-2xs">
                                    <div className="py-3 px-5 flex justify-between items-center gap-x-5 border-b border-card-header">
                                        <h2 className="inline-block font-semibold text-foreground">
                                            Edit Product
                                        </h2>
                                    </div>

                                    <div className="p-5 space-y-5">
                                        <div>
                                            <label htmlFor="prod_name" className="block mb-2 text-sm font-medium text-foreground">
                                                Product Name
                                            </label>
                                            <input
                                                id="prod_name"
                                                type="text"
                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg sm:text-sm text-layer-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus"
                                                value={form.prod_name}
                                                onChange={(e) => setField("prod_name", e.target.value)}
                                            />
                                            {getError("prod_name") && (
                                                <p className="mt-1 text-xs text-red-600">{getError("prod_name")}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="prod_status" className="block mb-2 text-sm font-medium text-foreground">
                                                Product Status
                                            </label>
                                            <select
                                                id="prod_status"
                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg sm:text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                value={form.prod_status}
                                                onChange={(e) => setField("prod_status", Number(e.target.value))}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                            {getError("prod_status") && (
                                                <p className="mt-1 text-xs text-red-600">{getError("prod_status")}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="prod_image" className="block mb-2 text-sm font-medium text-foreground">
                                                Product Image
                                            </label>
                                            <input
                                                id="prod_image"
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg sm:text-sm text-layer-foreground file:me-3 file:py-1.5 file:px-3 file:border-0 file:rounded-md file:bg-primary file:text-primary-foreground"
                                                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                                            />
                                            {imagePreview && (
                                                <img
                                                    src={imagePreview}
                                                    alt="Product preview"
                                                    className="mt-3 h-28 w-28 rounded-lg object-cover border border-card-line"
                                                />
                                            )}
                                            {initialImageUrl && (
                                                <label className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.remove_prod_image}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                remove_prod_image: checked,
                                                                prod_image: checked ? null : prev.prod_image,
                                                            }));
                                                            if (checked) {
                                                                setImagePreview(null);
                                                            } else if (!form.prod_image) {
                                                                setImagePreview(initialImageUrl);
                                                            }
                                                        }}
                                                    />
                                                    Remove current image
                                                </label>
                                            )}
                                            {getError("prod_image") && (
                                                <p className="mt-1 text-xs text-red-600">{getError("prod_image")}</p>
                                            )}
                                        </div>

                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <h3 className="text-sm font-medium text-foreground">Variants</h3>
                                                <button
                                                    type="button"
                                                    onClick={addVariant}
                                                    className="py-1.5 px-3 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                                                >
                                                    Add Variant
                                                </button>
                                            </div>

                                            {form.variants.map((variant, index) => (
                                                <div key={`${variant.prod_var_id ?? "new"}-${index}`} className="p-3 border border-card-line rounded-lg space-y-3">
                                                    {Number(variant.prod_var_type) === 1 && (
                                                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                                            This custom variant is locked and cannot be edited.
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Variant Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={variant.prod_var_name}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_name", e.target.value)
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                                placeholder="e.g. 250g"
                                                            />
                                                            {getError(`variants.${index}.prod_var_name`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_name`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Total Earned Coin
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={variant.total_earned_coin}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "total_earned_coin", e.target.value)
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            />
                                                            {getError(`variants.${index}.total_earned_coin`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.total_earned_coin`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Price
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={variant.prod_var_price}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_price", e.target.value)
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            />
                                                            {getError(`variants.${index}.prod_var_price`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_price`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                {Number(variant.prod_var_bonus_type) === 0 ? "Bonus (%)" : "Bonus (Rp)"}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={variant.prod_var_bonus}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_bonus", e.target.value)
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            />
                                                            <p className="mt-1 text-xs text-muted-foreground-1">
                                                                {Number(variant.prod_var_bonus_type) === 0
                                                                    ? "Use percentage value, e.g. 10 for 10%"
                                                                    : "Use nominal value in Rupiah"}
                                                            </p>
                                                            {getError(`variants.${index}.prod_var_bonus`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_bonus`)}
                                                            </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Bonus Type
                                                            </label>
                                                            <select
                                                                value={variant.prod_var_bonus_type}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_bonus_type", Number(e.target.value))
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            >
                                                                <option value={0}>Percent</option>
                                                                <option value={1}>Nominal</option>
                                                            </select>
                                                            {getError(`variants.${index}.prod_var_bonus_type`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_bonus_type`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Reseller Comm. Status
                                                            </label>
                                                            <select
                                                                value={variant.reseller_commission_status}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "reseller_commission_status", Number(e.target.value))
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            >
                                                                <option value={0}>Disabled</option>
                                                                <option value={1}>Enabled</option>
                                                            </select>
                                                            {getError(`variants.${index}.reseller_commission_status`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.reseller_commission_status`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Reseller Comm. Type
                                                            </label>
                                                            <select
                                                                value={variant.reseller_commission_type}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "reseller_commission_type", Number(e.target.value))
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            >
                                                                <option value={2}>Percent</option>
                                                                <option value={1}>Fixed</option>
                                                            </select>
                                                            {getError(`variants.${index}.reseller_commission_type`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.reseller_commission_type`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                {Number(variant.reseller_commission_type) === 2
                                                                    ? "Reseller Comm. (%)"
                                                                    : "Reseller Comm. (Rp)"}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={variant.reseller_commission_value}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "reseller_commission_value", e.target.value)
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            />
                                                            {getError(`variants.${index}.reseller_commission_value`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.reseller_commission_value`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Variant Type
                                                            </label>
                                                            <select
                                                                value={variant.prod_var_type}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_type", Number(e.target.value))
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            >
                                                                <option value={0}>Normal</option>
                                                                <option value={1}>Custom</option>
                                                            </select>
                                                            {getError(`variants.${index}.prod_var_type`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_type`)}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div>
                                                            <label className="block mb-1 text-xs font-medium text-muted-foreground-1">
                                                                Status
                                                            </label>
                                                            <select
                                                                value={variant.prod_var_status}
                                                                disabled={Number(variant.prod_var_type) === 1}
                                                                onChange={(e) =>
                                                                    setVariantField(index, "prod_var_status", Number(e.target.value))
                                                                }
                                                                className="py-2 px-3 block w-full bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground focus:border-primary-focus focus:ring-primary-focus"
                                                            >
                                                                <option value={1}>Active</option>
                                                                <option value={0}>Inactive</option>
                                                            </select>
                                                            {getError(`variants.${index}.prod_var_status`) && (
                                                                <p className="mt-1 text-xs text-red-600">
                                                                    {getError(`variants.${index}.prod_var_status`)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button
                                                            type="button"
                                                            disabled={form.variants.length === 1 || Number(variant.prod_var_type) === 1}
                                                            onClick={() => removeVariant(index)}
                                                            className="py-1.5 px-3 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:pointer-events-none"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => router.visit(`/panel/products/${data.prod_id}`)}
                                        className="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        {submitting ? "Saving..." : "Update Product"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </main>
        </>
    );
}
